---
title: Conventions
description: The rules every endpoint follows, from money and pagination to errors, markets and caching.
order: 3
---

These rules hold across the whole Storefront API, so the endpoint pages do not repeat them. The
examples reuse `NUCLEUS_API_URL`, `NUCLEUS_KEY` and the SDK client `storefront` from
[Getting started](/docs).

## Requests and responses

- Bodies are JSON in both directions, with `camelCase` field names. Send
  `Content-Type: application/json` with every body.
- A single resource comes back as a bare object and a list as `{ items, nextCursor }`; there is no
  `data` wrapper.
- Ids are UUID strings. Products, collections, pages, posts and menus are also addressed by
  `handle`, which is what storefront URLs use.
- Timestamps are ISO 8601 in UTC, for example `2026-09-30T18:44:50.954Z`.

## Money

Every amount is an object with an integer `amount` in the currency's minor units and an ISO 4217
`currency`: `{ "amount": 99000, "currency": "USD" }` is $990.00. Never parse amounts as floats;
convert only when you display them.

```ts title="Formatting money"
interface Money {
  amount: number;
  currency: string;
}

function formatMoney({ amount, currency }: Money, locale = 'en-US') {
  const format = new Intl.NumberFormat(locale, { style: 'currency', currency });
  // 2 for USD and EUR, 0 for JPY: the currency's minor units.
  const digits = format.resolvedOptions().maximumFractionDigits ?? 2;
  return format.format(amount / 10 ** digits);
}

formatMoney({ amount: 99000, currency: 'USD' }); // "$990.00"
```

Prices come back already converted to the market's currency (see [Markets](#markets-and-country)),
so a storefront never converts currencies itself.

## Pagination

Lists take `limit` (1 to 100, default 20) and `cursor`. The response's `nextCursor` goes into the
next request's `cursor`; it is `null` on the last page. Treat cursors as opaque strings: a cursor
from another list or a hand-edited one fails with `400 INVALID_CURSOR`.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/products?limit=2" \
  -H "Authorization: Bearer $NUCLEUS_KEY"

# Next page: pass the nextCursor from the previous response.
curl "$NUCLEUS_API_URL/storefront/v1/products?limit=2&cursor=$NEXT_CURSOR" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
interface ProductPage {
  items: { handle: string }[];
  nextCursor: string | null;
}

const handles: string[] = [];
let cursor: string | null = null;
do {
  const query = new URLSearchParams({ limit: '100', ...(cursor ? { cursor } : {}) });
  const page: ProductPage = await fetch(`${NUCLEUS_API_URL}/storefront/v1/products?${query}`, {
    headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
  }).then((response) => response.json());
  handles.push(...page.items.map((product) => product.handle));
  cursor = page.nextCursor;
} while (cursor);
```

```ts title="SDK"
const titles: string[] = [];
let next: string | null = null;
do {
  const page = await storefront.listProducts({ limit: 100, ...(next ? { cursor: next } : {}) });
  titles.push(...page.items.map((product) => product.title));
  next = page.nextCursor;
} while (next);
```

</CodeTabs>

## Errors

Every response that is not `2xx` has the same JSON body:

```json title="422 from POST /carts/{token}/lines"
{
  "error": {
    "code": "INVALID_INPUT",
    "message": "Ring size is required",
    "requestId": "01a0f3a4-c3c3-7f5f-a217-7ff76ee7e950",
    "details": [
      {
        "field": "body.customizations",
        "code": "INVALID_INPUT",
        "message": "Ring size is required"
      }
    ],
    "meta": { "missing": "ring_size" }
  }
}
```

| Field       | Always | Meaning                                                                                               |
| ----------- | ------ | ----------------------------------------------------------------------------------------------------- |
| `code`      | yes    | Stable, upper-case code from the table below. Switch on it, never on `message`                        |
| `message`   | yes    | Readable English sentence; safe to show, but translate by `code` for shoppers                         |
| `requestId` | yes    | Same as the `x-request-id` header. Quote it when you report a problem                                 |
| `details`   | no     | One entry per invalid input: `field` is prefixed by where it came from (`body.`, `query.`, `params.`) |
| `meta`      | no     | Extras a client can act on, documented per code (`scope`, `reason`, `retryAfterSeconds`)              |

A code always comes with the same status, so a client that does not know a code can fall back on
the status.

<CodeTabs>

```ts title="fetch"
const response = await fetch(`${NUCLEUS_API_URL}/storefront/v1/products?limit=500`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
});
if (!response.ok) {
  const { error } = await response.json();
  // error.code === 'VALIDATION_ERROR'
  // error.details[0] -> { field: 'query.limit', code: 'too_big', message: '...' }
  console.warn(error.code, error.requestId);
}
```

```ts title="SDK"
import { StorefrontApiError, fieldErrors } from '@nucleus-commerce/storefront-sdk';

try {
  await storefront.createCart({ lines: [{ variantId: 'not-a-uuid', quantity: 0 }] });
} catch (error) {
  if (error instanceof StorefrontApiError && error.code === 'VALIDATION_ERROR') {
    // { 'lines.0.variantId': 'Invalid UUID', 'lines.0.quantity': 'Too small: ...' }
    const fields = fieldErrors(error);
    console.warn(fields, error.requestId);
  }
}
```

</CodeTabs>

The SDK throws a `StorefrontApiError` with `code`, `status`, `requestId`, `details` and `meta`.
`fieldErrors` turns the details into `{ [field]: message }` for a form, with the `body.` prefix
removed so the keys match your request body.

### Error codes

The codes a storefront can meet. The downloadable OpenAPI document on the
[Postman page](/docs/postman) lists every code as an enum.

| Status | Code                           | When                                                                            |
| ------ | ------------------------------ | ------------------------------------------------------------------------------- |
| 400    | `VALIDATION_ERROR`             | A body, path or query value failed its schema; `details` lists every problem    |
| 400    | `INVALID_JSON`                 | The body is not valid JSON                                                      |
| 400    | `INVALID_CURSOR`               | The pagination cursor is malformed or from another list                         |
| 401    | `UNAUTHORIZED`                 | No API key, or no customer token on a shopper endpoint                          |
| 401    | `INVALID_API_KEY`              | The key is unknown or revoked                                                   |
| 401    | `INVALID_CREDENTIALS`          | Wrong email or password (the same answer for both)                              |
| 401    | `TOKEN_EXPIRED`                | The customer access token expired: refresh and retry                            |
| 401    | `TOKEN_INVALID`                | A customer token, refresh token or one-time link is invalid or used             |
| 403    | `ORIGIN_NOT_ALLOWED`           | The request's origin, or a `redirectUrl`/`resetUrl`, is not on the key          |
| 403    | `SECRET_KEY_IN_BROWSER`        | A secret key was sent from a browser                                            |
| 403    | `PERMISSION_DENIED`            | The key's scopes do not include this endpoint; `meta.scope` names the scope     |
| 403    | `STORE_CLOSED`                 | The store behind the key is closed                                              |
| 403    | `ACCOUNT_INACTIVE`             | The shopper's account is disabled                                               |
| 404    | `NOT_FOUND`                    | No such product, cart, order, ...; also for records of another store            |
| 404    | `ROUTE_NOT_FOUND`              | No endpoint at this method and path: check the URL                              |
| 409    | `CONFLICT`                     | The resource's state does not allow this, e.g. a return before anything shipped |
| 409    | `ALREADY_EXISTS`               | A unique value is taken, e.g. an email at registration                          |
| 409    | `INSUFFICIENT_STOCK`           | Not enough stock for the quantity                                               |
| 409    | `PAYMENT_NOT_COMPLETED`        | The Stripe payment has not succeeded yet; `meta.status` says where it is        |
| 409    | `PAYMENT_PROVIDER_UNAVAILABLE` | The store has no connected Stripe account                                       |
| 409    | `GOOGLE_SIGN_IN_UNAVAILABLE`   | Google sign-in is not configured                                                |
| 409    | `TRANSACTION_CONFLICT`         | Two requests collided; retry                                                    |
| 413    | `PAYLOAD_TOO_LARGE`            | The body is over 1 MB                                                           |
| 422    | `INVALID_INPUT`                | Well-formed input that breaks a business rule; `details` and `meta` say which   |
| 422    | `DISCOUNT_NOT_APPLICABLE`      | The code is unknown or does not apply; `meta.reason` says why                   |
| 422    | `PAYMENT_PROVIDER_REJECTED`    | Stripe refused the request                                                      |
| 429    | `RATE_LIMITED`                 | Too many requests; wait `meta.retryAfterSeconds`                                |
| 500    | `INTERNAL_ERROR`               | Unexpected failure; quote the `requestId`                                       |
| 503    | `SERVICE_UNAVAILABLE`          | A dependency is down; retry after `Retry-After`                                 |

## Idempotency

No storefront endpoint requires an `Idempotency-Key` header today. An endpoint that starts to
require one will list the header on its reference page and answer `400 IDEMPOTENCY_KEY_REQUIRED`
without it.

The calls where a double submit would hurt are already safe to repeat:

<EndpointLink id="createPaymentIntent" /> returns the checkout's existing PaymentIntent, and
<EndpointLink id="completeCheckout" /> on a checkout that already became an order answers that same
order instead of placing a second one.

## Markets and country

A market groups countries with a currency and prices. Catalog reads and carts price for the
market that contains the shopper's country, and answer with the `market` they used. Without a
country, or for a country no market covers, the store's default market applies.

Pass the country as an upper-case ISO 3166 code in the `country` query parameter, or send the
`X-Nucleus-Country` header instead (from server code or the browser); the query parameter wins when
both are present. A cart takes `country` when it is created and switches market with

<EndpointLink id="updateCart" />.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/products/drift-diamond-pendant?country=US" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const pendant = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/products/drift-diamond-pendant?country=US`,
  { headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
).then((response) => response.json());
// pendant.market -> { id, name, currency: 'USD', countries: ['US'] }
```

```ts title="SDK"
const product = await storefront.getProduct({ handle: 'drift-diamond-pendant' }, { country: 'US' });
```

</CodeTabs>

## Caching

Reads that change rarely tell the browser it may keep them for a minute; anything about a shopper,
a cart or stock is never cached.

| Endpoints                                         | `Cache-Control`                                   |
| ------------------------------------------------- | ------------------------------------------------- |
| Store, pages, posts, menus                        | `private, max-age=60, stale-while-revalidate=600` |
| Products, collections, sections, blocks           | `private, max-age=60, stale-while-revalidate=300` |
| Search                                            | `private, max-age=30`                             |
| Availability, carts, checkouts, customers, orders | `no-store`                                        |

<EndpointLink id="getSection" />, <EndpointLink id="getBlock" /> and
<EndpointLink id="listBlocks" /> also send an `ETag`. Send it back as `If-None-Match` and an
unchanged response comes back as an empty `304 Not Modified`. A browser's HTTP cache does this on
its own (and does not show the `ETag` header to storefront JavaScript); do it yourself in server
code that keeps its own cache.

<CodeTabs>

```bash title="curl"
curl -i "$NUCLEUS_API_URL/storefront/v1/sections/home" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
# etag: W/"h3VxHl1MLDvXBdur2GGy4Xj_OXo"

curl -i "$NUCLEUS_API_URL/storefront/v1/sections/home" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H 'If-None-Match: W/"h3VxHl1MLDvXBdur2GGy4Xj_OXo"'
# HTTP/1.1 304 Not Modified
```

```ts title="fetch (server)"
const first = await fetch(`${NUCLEUS_API_URL}/storefront/v1/sections/home`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
});
const etag = first.headers.get('etag') ?? '';
const home = await first.json();

const again = await fetch(`${NUCLEUS_API_URL}/storefront/v1/sections/home`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'If-None-Match': etag },
});
// again.status === 304: keep using `home`.
```

</CodeTabs>

## Request ids

Every response, success or error, has an `x-request-id` header; error bodies repeat it as
`requestId`. Log it next to failures and quote it to support: every server log line for the
request carries it.
